import { setAppState } from '@cedalo/webui/src/actions/actions';
import { Overlay } from '@cedalo/webui/src/components/HelperComponent/Overlay';
import gatewayClient from '@cedalo/webui/src/helper/GatewayClient';
import { useGraphQL } from '@cedalo/webui/src/helper/Hooks';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CheckIcon from '@material-ui/icons/Check';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

// const GATEWAY_CONFIG = ConfigManager.config.gatewayClientConfig;

const HTTP_SERVER_SETTINGS_QUERY = `
	query HttpSererSettings($machineId: ID!) {
		scopedByMachine(machineId: $machineId) {
			machine(id: $machineId) {
				httpServer {
					enabled
					streamsheetId
					expectResponse
					path
					auth {
						username
						password
					}
				}
				streamsheets {
					id
					name
				}
			}
		}
	}
`;

const HTTP_SERVER_SETTINGS_MUTATION = `
	mutation UpdateHttpServerSettings($machineId: ID!, $httpServer: HttpServerInput!) {
		scopedByMachine(machineId: $machineId) {
			updateMachineHttpServer(machineId: $machineId, httpServer: $httpServer) {
				success
				code
				httpServer {
					enabled
					streamsheetId
					expectResponse
					path
					auth {
						username
						password
					}
				}
			}
		}
	}
`;

const getHostOrigin = () => {
	if (!window.location) throw new Error('Failed to determine host origin!');
	return window.location.origin;
};

const doUpdateHttpServer = async (machineId, httpServerUpdate) => {
	try {
		const data = await gatewayClient.graphql(HTTP_SERVER_SETTINGS_MUTATION, {
			machineId,
			httpServer: httpServerUpdate
		});
		const { success, code, httpServer } = data.scopedByMachine.updateMachineHttpServer;
		if (!success) {
			throw code;
		}
		return { success, httpServer };
	} catch (error) {
		console.error(`Failed to update HTTP server settings: ${httpServerUpdate}`, error);
		return { success: false };
	}
};

const Overlayed = (props) => {
	const { requestState } = props;
	const [reallyDone, setReallyDone] = useState(false);

	useEffect(() => {
		if (requestState === 'fetching') {
			setReallyDone(false);
		} else if (requestState === 'fetched') {
			setTimeout(() => setReallyDone(true), 500);
		}
	}, [requestState]);

	useEffect(() => {
		if (reallyDone) {
			props.doneCb();
		}
	}, [reallyDone]);

	if (requestState === 'fetching') {
		return (
			<Overlay>
				<CircularProgress style={{ width: '24px', height: '24px' }} />
			</Overlay>
		);
	}
	if (requestState === 'fetched') {
		return (
			<Overlay>
				<CheckIcon color="primary" />
			</Overlay>
		);
	}
	return null;
};

export const HttpServerSettingsDialog = connect(({ machine }) => ({ machineId: machine.id }), {
	setOpen: (open) => setAppState({ showHttpServerSettingsDialog: open })
})((props) => {
	const { machineId, open } = props;
	const [httpServer, setHttpServer] = useState(null);
	const [streamsheets, setStreamsheets] = useState(null);
	const [requireAuth, setRequireAuth] = useState(false);
	const [auth, setAuth] = useState({});
	const [requestState, setRequestState] = useState('not-fetched');
	// const { data, loading, errors } = useGraphQL(
	const { data } = useGraphQL(HTTP_SERVER_SETTINGS_QUERY, { machineId }, [machineId, props.open], () => {
		return props.open;
	});

	const onClose = () => props.setOpen(false);

	const setEnabled = async (enabled) => {
		if (!httpServer.id) {
			const result = await doUpdateHttpServer(machineId, { enabled });
			if (result.httpServer) {
				setHttpServer({ ...result.httpServer });
			}
		} else {
			setHttpServer({ ...httpServer, enabled });
		}
	};

	const onSave = async () => {
		setRequestState('fetching');
		const { path, ...httpServerUpdate } = httpServer;
		await doUpdateHttpServer(machineId, { ...httpServerUpdate, auth: requireAuth ? auth : null });
		setRequestState('fetched');
	};

	useEffect(() => {
		const machine = data && data.scopedByMachine.machine;
		if (machine) {
			setHttpServer(machine.httpServer);
			setStreamsheets(machine.streamsheets);
			setAuth(machine.httpServer.auth || auth);
			setRequireAuth(!!machine.httpServer.auth);
		} else {
			setRequestState('not-fetched');
			setHttpServer(null);
			setAuth({});
			setRequireAuth(false);
		}
	}, [data]);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				<FormattedMessage id="Extensions.HttpServer.Dialog.Title" defaultMessage="HTTP Server Settings" />
			</DialogTitle>
			<DialogContent style={{ paddingTop: '0px', marginTop: '15px' }}>
				<Overlayed requestState={requestState} doneCb={onClose} />
				{httpServer ? (
					<React.Fragment>
						<FormControl margin="dense">
							<FormControlLabel
								control={
									<Checkbox
										color="primary"
										checked={httpServer.enabled}
										onChange={(ev, checked) => setEnabled(checked)}
									/>
								}
								label={
									<FormattedMessage
										id="Extensions.HttpServer.Dialog.Enabled"
										defaultMessage="Enable HTTP Server"
									/>
								}
							/>
						</FormControl>
						{httpServer.enabled ? (
							<React.Fragment>
								<TextField
									variant="outlined"
									margin="dense"
									size="small"
									fullWidth
									label={
										<FormattedMessage
											id="Extensions.HttpServer.Dialog.Streamsheet"
											defaultMessage="Streamsheet"
										/>
									}
									select
									helperText={
										<FormattedMessage
											id="Extensions.HttpServer.Dialog.Streamsheet.Help"
											defaultMessage="Streamsheet which receives the messages in the inbox"
										/>
									}
									value={httpServer.streamsheetId}
									onChange={(event) =>
										setHttpServer({ ...httpServer, streamsheetId: event.target.value })
									}
								>
									{streamsheets.map((streamsheet) => (
										<MenuItem value={streamsheet.id} key={streamsheet.id}>
											{streamsheet.name}
										</MenuItem>
									))}
								</TextField>
								{httpServer.path ? (
									<React.Fragment>
										<Typography
											color="textSecondary"
											display="block"
											variant="caption"
											style={{ fontSize: '0.65rem' }}
										>
											<FormattedMessage
												id="Extensions.HttpServer.Dialog.Url"
												defaultMessage="URL"
											/>
										</Typography>
										<TextField
											type="text"
											fullWidth
											value={`${getHostOrigin()}${httpServer.path}`}
											InputLabelProps={{ shrink: true }}
											InputProps={{ readOnly: true }}
											helperText={
												<FormattedMessage
													id="Extensions.HttpServer.Dialog.Url.Help"
													defaultMessage="Use this URL to send messages to the configured Streamsheet via HTTP. Any additional path segments will be part of the messages metadata."
												/>
											}
										/>
									</React.Fragment>
								) : null}
								<FormControl margin="dense">
									<FormControlLabel
										control={
											<Checkbox
												color="primary"
												checked={httpServer.expectResponse}
												onChange={(ev, checked) =>
													setHttpServer({ ...httpServer, expectResponse: checked })
												}
											/>
										}
										label={
											<FormattedMessage
												id="Extensions.HttpServer.Dialog.ExpectResponse"
												defaultMessage="Expect Response"
											/>
										}
									/>
									<FormHelperText>
										<FormattedMessage
											id="Extensions.HttpServer.Dialog.ExpectResponse.Help"
											defaultMessage="Send a custom HTTP response by using the HTTP.RESPOND2 Streamsheet function."
										/>
									</FormHelperText>
								</FormControl>
								<FormControl margin="dense">
									<FormControlLabel
										control={
											<Checkbox
												color="primary"
												checked={requireAuth}
												onChange={(ev, checked) => setRequireAuth(checked)}
											/>
										}
										label={
											<FormattedMessage
												id="Extensions.HttpServer.Dialog.RequireAuthentication"
												defaultMessage="Require Authentication"
											/>
										}
									/>
									<FormHelperText>
										<FormattedMessage
											id="Extensions.HttpServer.Dialog.RequireAuthentication.Help"
											defaultMessage={`Require "Basic Auth" for all HTTP requests.`}
										/>
									</FormHelperText>
								</FormControl>
								{requireAuth ? (
									<React.Fragment>
										<TextField
											id="username"
											margin="dense"
											label={
												<FormattedMessage
													id="Extensions.HttpServer.Dialog.Username"
													defaultMessage="Username"
												/>
											}
											type="text"
											fullWidth
											value={auth.username || ''}
											onChange={(ev) => setAuth({ ...auth, username: ev.target.value })}
										/>
										<TextField
											id="password"
											margin="dense"
											label={
												<FormattedMessage
													id="Extensions.HttpServer.Dialog.Password"
													defaultMessage="Password"
												/>
											}
											type="text"
											fullWidth
											value={auth.password || ''}
											onChange={(ev) => setAuth({ ...auth, password: ev.target.value })}
										/>
										{!(auth.username && auth.password) ? (
											<Typography color="error" style={{ marginTop: '8px' }}>
												<FormattedMessage
													id="Extensions.HttpServer.Dialog.Error.UsernamePasswordRequired"
													defaultMessage="Username and password required!"
												/>
											</Typography>
										) : null}
									</React.Fragment>
								) : null}
							</React.Fragment>
						) : null}
					</React.Fragment>
				) : null}
			</DialogContent>
			<DialogActions
			// style={{ justifyContent: 'space-between' }}
			>
				<Button onClick={onClose} variant="contained">
					<FormattedMessage id="Extensions.Button.Close" defaultMessage="Close" />
				</Button>
				<Button
					onClick={onSave}
					variant="contained"
					color="primary"
					disabled={requireAuth && !(auth.username && auth.password)}
				>
					<FormattedMessage id="Extensions.Button.Save" defaultMessage="Save" />
				</Button>
			</DialogActions>
		</Dialog>
	);
});



// WEBPACK FOOTER //
// /home/travis/build/cedalo/streamsheets-pro-features/packages/webui-extensions/src/http-server/HttpServerSettingsDialog.js